import { useAuth0 } from '@auth0/auth0-react';
import { useCopyToClipboard } from '@base/hooks/use-copy-to-clipboard';
import RoundButton from '@basic/RoundButton';
import { DISCORD_INVITE_URL, UIPaths } from '@consts';
import { useLikesService } from '@hooks/useLikeService';
import { useShareReferralCode } from '@hooks/useShareReferralCode';
import ChevronLeftIcon from '@icons/ChevronLeftIcon';
import DiscordIcon from '@icons/DiscordIcon';
import HeartEmptyIcon from '@icons/HeartEmptyIcon';
import HeartFullIcon from '@icons/HeartFullIcon';
import ShareIcon from '@icons/ShareIcon';
import { ToastType } from '@models/common';
import { useStore } from '@store';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { usePaymentAndOrderOptional } from '../../payments/PaymentAndOrderContext';
import GoalLandingPageJumpToMainCTAButton from './GoalLandingPageJumpToMainCTAButton';
import { useLandingPageTranslation } from './useLandingPageTranslation';

const scrollToId = (target: string) => {
  document.getElementById(target.replace('#', ''))?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
};

const ICON_BASE_CLASS = 'hover:opacity-80 cursor-pointer hover:scale-105 transition-all';
const GOAL_ICON_CLASS = classNames(ICON_BASE_CLASS, setupForDark('text-icon-primary'));
const PACKAGE_ICON_CLASS = classNames(ICON_BASE_CLASS, 'text-black');

const DiscordLink = ({ label, className }: { label: string; className: string }) => (
  <a href={DISCORD_INVITE_URL} target='_blank' rel='noopener noreferrer' aria-label={label}>
    <DiscordIcon size={32} className={className} />
  </a>
);

function MenuNavLink({
  target,
  label,
  className
}: {
  target: string;
  label: string;
  className: string;
}) {
  return (
    <div
      onClick={() => scrollToId(target)}
      className={classNames('P2M hover:opacity-80 cursor-pointer', className)}
    >
      {label}
    </div>
  );
}

const PACKAGE_NAV = [
  { target: '#kursy', key: 'courses' },
  { target: '#pricing', key: 'pricing' },
  { target: '#faq', key: 'faq' }
] as const;

const GOAL_NAV = [
  { target: '#mentor', key: 'aboutMentor' },
  { target: '#toc', key: 'tableOfContents' },
  { target: '#pricing', key: 'pricing' },
  { target: '#faq', key: 'faq' }
] as const;

const BackButton: React.FC = () => {
  const router = useRouter();

  const handleBack = () => {
    const referrer = document.referrer;
    const currentHost = window.location.host;

    let isInternalNavigation = false;
    if (referrer) {
      try {
        isInternalNavigation = new URL(referrer).host === currentHost;
      } catch {
        // Invalid URL in referrer, treat as external
        isInternalNavigation = false;
      }
    }

    if (isInternalNavigation) {
      router.back();
    } else {
      router.push(UIPaths.explore());
    }
  };

  return (
    <div
      onClick={handleBack}
      className={classNames('P2M hover:opacity-80 cursor-pointer', setupForDark('text-primary'))}
    >
      <ChevronLeftIcon size={32} />
    </div>
  );
};

export default function GoalLandingPageMenuBar({
  hideActions = false,
  goalPackage = false
}: {
  hideActions?: boolean;
  goalPackage?: boolean;
}) {
  const ctx = usePaymentAndOrderOptional();
  const state = ctx?.state;
  const isSoldOut = ctx?.isSoldOut ?? false;
  const { t } = useLandingPageTranslation();
  const { likeGoal, unlikeGoal } = useLikesService();
  const [liked, setLiked] = useState(state?.goal?.liked?.isActive);
  const { copy } = useCopyToClipboard();
  const shareReferralCode = useShareReferralCode();
  const { setToast } = useStore((state) => ({
    setToast: state.setToast
  }));
  const { isAuthenticated, loginWithRedirect } = useAuth0();

  const onUnlike = (event) => {
    event.preventDefault();
    event.stopPropagation();

    unlikeGoal(state?.goal?.entityId)
      .then((success) => {
        setLiked(false);
      })
      .catch((error) => {
        setToast({ message: t('toasts.somethingWentWrong'), type: ToastType.ERROR });
      });
  };

  const onLike = (event) => {
    event.preventDefault();
    event.stopPropagation();

    likeGoal(state?.goal?.entityId)
      .then((success) => {
        setLiked(true);
      })
      .catch((error) => {
        setToast({ message: t('toasts.somethingWentWrong'), type: ToastType.ERROR });
      });
  };

  useEffect(() => {
    setLiked(state?.goal?.liked?.isActive);
  }, [state?.goal?.liked]);

  const navLink = (item: { target: string; key: string }, className: string) => (
    <MenuNavLink
      key={item.target + item.key}
      target={item.target}
      label={t(`domains.goals.landingPage.menu.${item.key}`)}
      className={className}
    />
  );

  const leftSection = (() => {
    if (goalPackage) return PACKAGE_NAV.map((item) => navLink(item, 'text-black'));
    if (isSoldOut) return isAuthenticated && <BackButton />;
    return (
      <>
        {isAuthenticated && <BackButton />}
        {GOAL_NAV.map((item) => navLink(item, setupForDark('text-primary')))}
      </>
    );
  })();

  const handlePackageShare = (event: React.MouseEvent): void => {
    const url = new URL(window.location.href);
    url.searchParams.delete('ref');
    if (shareReferralCode) url.searchParams.set('ref', shareReferralCode);
    copy(url.toString(), event);
  };

  const packageShare = (
    <div className='flex items-center gap-md'>
      <DiscordLink
        label={t('domains.goals.landingPage.menu.discord')}
        className={PACKAGE_ICON_CLASS}
      />
      <button
        type='button'
        data-testid='package-landing-share-button'
        aria-label={t('domains.goals.landingPage.menu.share')}
        onClick={handlePackageShare}
        className='flex p-none'
      >
        <ShareIcon size={32} className={PACKAGE_ICON_CLASS} />
      </button>
    </div>
  );

  const goalActions = !hideActions && (
    <div
      className={classNames(
        'flex items-center',
        // RESPONSIVE STYLES
        'justify-between @[640px]:justify-start gap-md @[1024px]:gap-xxxl w-full @[640px]:w-auto'
      )}
    >
      {/* Actions */}
      <div className='flex gap-xl @[1024px]:gap-xxxl'>
        <DiscordLink
          label={t('domains.goals.landingPage.menu.discord')}
          className={GOAL_ICON_CLASS}
        />

        <button
          type='button'
          data-testid='goal-landing-share-button'
          aria-label={t('domains.goals.landingPage.menu.share')}
          onClick={(event) =>
            copy(UIPaths.shareableGoal(state?.goal?.entityId, shareReferralCode), event)
          }
          className='flex p-none'
        >
          <ShareIcon size={32} className={GOAL_ICON_CLASS} />
        </button>

        {isAuthenticated && (
          <>
            {liked ? (
              <HeartFullIcon size={32} className={GOAL_ICON_CLASS} onClick={onUnlike} />
            ) : (
              <HeartEmptyIcon size={32} className={GOAL_ICON_CLASS} onClick={onLike} />
            )}
          </>
        )}
      </div>

      <div
        className={classNames(
          'w-fit',
          // RESPONSIVE STYLES
          'max-w-[200px] @[640px]:max-w-none'
        )}
      >
        {isAuthenticated ? (
          <GoalLandingPageJumpToMainCTAButton type='menu' />
        ) : (
          <RoundButton
            onClick={() =>
              loginWithRedirect({
                appState: { returnTo: window.location.pathname + window.location.search }
              })
            }
            type='empty'
            size='medium'
            className='w-full @[640px]:w-auto min-w-[127px]'
          >
            {t('domains.payments.CTAs.login')}
          </RoundButton>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={classNames(
        'w-full max-w-[1200px] mx-auto flex items-center justify-between py-xl',
        // RESPONSIVE STYLES
        'flex-col @[640px]:flex-row',
        'gap-md @[640px]:gap-md',
        'px-sm @[640px]:px-xl @[1280px]:px-none'
      )}
    >
      <div
        className={classNames(
          'flex items-center',
          // RESPONSIVE STYLES
          'gap-md @[1024px]:gap-xxxl'
        )}
      >
        {leftSection}
      </div>

      {goalPackage ? packageShare : goalActions}
    </div>
  );
}
